import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { GET, POST, DELETE, loading, alert, Select, saveAs, format_date } from "./CustomComponent";

const docTypeDefaultOptions = [
  { value: "id_card", label: "บัตรประชาชน" },
  { value: "contract", label: "สัญญาจ้าง" },
  { value: "certificate", label: "ใบประกาศนียบัตร/วุฒิบัตร" },
  { value: "other", label: "อื่นๆ" },
];

const panelLabelStyle = {
  color: "#0b4f84",
  fontSize: "0.82rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentManagerPanel = ({ ownerType, ownerUid, docTypeOptions = docTypeDefaultOptions }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [docType, setDocType] = useState("");

  const GetData = useCallback(async () => {
    if (!ownerType || !ownerUid) return;
    loading(true);
    const result = await GET("v1/admin/documents", { owner_type: ownerType, owner_uid: ownerUid });
    if (result?.status) {
      setDocuments(result.data || []);
    }
    loading(false);
  }, [ownerType, ownerUid]);

  useEffect(() => {
    GetData();
  }, [GetData]);

  const handleFilesChange = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาเลือกไฟล์ที่ต้องการอัปโหลด");
      return;
    }

    if (!(await alert("", "info", "ยืนยัน", "ต้องการอัปโหลดเอกสารนี้ใช่หรือไม่?", "ยืนยัน", "ยกเลิก"))) {
      return;
    }

    loading(true);
    try {
      const files = await Promise.all(
        selectedFiles.map(async (file) => ({
          file_name: file.name,
          mime_type: file.type,
          file_base64: await readFileAsBase64(file),
        }))
      );

      const result = await POST("v1/admin/documents", {
        owner_type: ownerType,
        owner_uid: ownerUid,
        doc_type: docType,
        files,
      }, null, { onProgress: (percent) => loading(true, percent) });

      if (result?.status === true) {
        alert("", "success", "สำเร็จ", "อัปโหลดเอกสารเรียบร้อยแล้ว");
        setSelectedFiles([]);
        setDocType("");
        const fileInput = document.getElementById(`document-upload-input-${ownerType}-${ownerUid}`);
        if (fileInput) fileInput.value = "";
        GetData();
      } else {
        alert("", "warning", "แจ้งเตือน", result?.message || "อัปโหลดเอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      }
    } finally {
      loading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (await alert("", "warning", "ลบรายการ", `ต้องการลบเอกสาร "${doc.file_name}" ใช่หรือไม่?`, "ยืนยัน", "ยกเลิก")) {
      loading(true);
      const result = await DELETE("v1/admin/documents/" + doc.uid, null);
      if (result?.status) {
        alert("", "success", "สำเร็จ", "ลบเอกสารเรียบร้อยแล้ว");
        GetData();
      }
      loading(false);
    }
  };

  const handleDownload = (doc) => {
    saveAs(doc.download_url, doc.file_name);
  };

  return (
    <div className="row g-4">
      <div className="col-12">
        <div
          className="p-4 rounded-4"
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #ebf6ff 100%)",
            border: "1px solid rgba(15, 102, 182, 0.16)",
          }}
        >
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label" style={panelLabelStyle}>ประเภทเอกสาร</label>
              <Select
                className="form-control"
                value={docType}
                options={docTypeOptions}
                placeholder="เลือกประเภทเอกสาร"
                onChange={(value) => setDocType(value)}
              />
            </div>
            <div className="col-md-5">
              <label className="form-label" style={panelLabelStyle}>เลือกไฟล์ (ภาพ หรือ PDF)</label>
              <input
                id={`document-upload-input-${ownerType}-${ownerUid}`}
                type="file"
                className="form-control"
                multiple
                accept="image/*,.pdf,application/pdf"
                onChange={handleFilesChange}
              />
            </div>
            <div className="col-md-3">
              <button type="button" className="btn btn-primary w-100" onClick={handleUpload}>
                <i className="fa-solid fa-cloud-arrow-up me-2" />
                อัปโหลดเอกสาร
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="table-responsive table-mobile-cards">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ชื่อไฟล์</th>
                <th>ประเภท</th>
                <th>ขนาด</th>
                <th>วันที่อัปโหลด</th>
                <th className="text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-secondary py-4">
                    ยังไม่มีเอกสาร
                  </td>
                </tr>
              )}
              {documents.map((doc) => (
                <tr key={doc.uid}>
                  <td data-label="ชื่อไฟล์">
                    <span>
                      <i className={`fa-solid ${doc.file_type === "pdf" ? "fa-file-pdf text-danger" : "fa-file-image text-primary"} me-2`} />
                      {doc.file_name}
                    </span>
                  </td>
                  <td data-label="ประเภท">{docTypeOptions.find((o) => o.value === doc.doc_type)?.label || doc.doc_type || "-"}</td>
                  <td data-label="ขนาด">{formatBytes(doc.file_size)}</td>
                  <td data-label="วันที่อัปโหลด" className="text-nowrap">{doc.created_at ? format_date(doc.created_at, "dd/mm/yyyy hh:mm") : "-"}</td>
                  <td data-label="จัดการ" className="text-center">
                    <button type="button" className="btn btn-sm btn-outline-primary rounded-3 px-3 me-2" title="ดูตัวอย่าง" onClick={() => window.open(doc.preview_url, "_blank")}>
                      <i className="fa-solid fa-eye" />
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-3 px-3 me-2" title="ดาวน์โหลด" onClick={() => handleDownload(doc)}>
                      <i className="fa-solid fa-download" />
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger rounded-3 px-3" title="ลบ" onClick={() => handleDelete(doc)}>
                      <i className="fa-solid fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const DocumentManagerModal = ({ show, onHide, ownerType, ownerUid, title = "จัดการเอกสาร", docTypeOptions }) => (
  <Modal show={show} onHide={onHide} centered size="xl" dialogClassName="modal-dialog-scrollable">
    <Modal.Header closeButton className="border-0 bg-light">
      <Modal.Title className="fw-bold h5 mb-0">{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {show && <DocumentManagerPanel ownerType={ownerType} ownerUid={ownerUid} docTypeOptions={docTypeOptions} />}
    </Modal.Body>
  </Modal>
);

export default DocumentManagerModal;
